import {
  SPECTRUM_FREQUENCY_SCALE,
  type TSignalDisplayUnit,
  type TSpectrumFrequencyScale,
} from "../../../multi-analysis/types";
import { getSpectrumAmplitudeFactor, isVibrationUnitCode } from "../../../multi-analysis/utils/signal-unit-conversion";
import type { ICascadeSpectrum, ICascadeTrendSeries } from "./model";

const HERTZ_UNIT_CODE = 3;
const ORDERS_UNIT_CODE = 4;
const CURSOR_LINE_COUNT = 5;

export const cascadeDisplayFrequency = (
  frequencyHz: number,
  frequencyUnitCode: number,
  rpm: number | null,
): number | null => {
  if (!Number.isFinite(frequencyHz) || frequencyHz <= 0) return null;
  if (frequencyUnitCode !== ORDERS_UNIT_CODE) return frequencyHz;
  if (rpm === null || !(rpm > 0)) return null;
  return frequencyHz / (rpm / 60);
};

export const cascadeFrequencyHz = (
  displayFrequency: number,
  frequencyUnitCode: number,
  rpm: number | null,
): number | null => {
  if (!Number.isFinite(displayFrequency) || displayFrequency <= 0) return null;
  if (frequencyUnitCode !== ORDERS_UNIT_CODE) return displayFrequency;
  if (rpm === null || !(rpm > 0)) return null;
  return displayFrequency * (rpm / 60);
};

export const scaleCascadeSpectrumFrequency = (
  spectrum: ICascadeSpectrum,
  scale: TSpectrumFrequencyScale,
  frequencyUnits: (unitCode: number) => string,
): ICascadeSpectrum => {
  const targetIsOrders = scale === SPECTRUM_FREQUENCY_SCALE.ORDERS;
  const sourceIsOrders = spectrum.frequencyUnitCode === ORDERS_UNIT_CODE;
  if (sourceIsOrders === targetIsOrders) return spectrum;
  const rpm = spectrum.rotationFrequencyRpm;
  if (rpm === null || !(rpm > 0)) return spectrum;
  const factor = targetIsOrders ? 60 / rpm : rpm / 60;
  const unitCode = targetIsOrders ? ORDERS_UNIT_CODE : HERTZ_UNIT_CODE;
  return {
    ...spectrum,
    frequencyUnitCode: unitCode,
    frequencyUnits: frequencyUnits(unitCode),
    items: spectrum.items.map(item => ({ x: item.x * factor, y: item.y })),
  };
};

export const cascadeHarmonicFrequencies = (base: number, max = CURSOR_LINE_COUNT): number[] => {
  if (!(base > 0)) return [];
  const lines: number[] = [];
  for (let order = 2; order <= max; order += 1) lines.push(base * order);
  return lines;
};

export const cascadeSubharmonicFrequencies = (base: number, max = CURSOR_LINE_COUNT): number[] => {
  if (!(base > 0)) return [];
  const lines: number[] = [];
  for (let divider = 2; divider <= max; divider += 1) lines.push(base / divider);
  return lines;
};

export const interpolateCascadeAmplitude = (
  items: readonly { x: number; y: number }[],
  frequency: number,
): number | null => {
  if (!items.length || !Number.isFinite(frequency)) return null;
  let previous = items[0];
  if (frequency < previous.x) return null;
  for (const item of items) {
    if (item.x === frequency) return item.y;
    if (item.x > frequency) {
      const span = item.x - previous.x;
      if (!(span > 0)) return previous.y;
      return previous.y + ((frequency - previous.x) / span) * (item.y - previous.y);
    }
    previous = item;
  }
  return frequency === previous.x ? previous.y : null;
};

export const sampleCascadeSpectrumAmplitude = (
  spectrum: Pick<ICascadeSpectrum, "frequencyUnitCode" | "items">,
  frequencyHz: number,
  referenceRpm: number | null,
): number | null => {
  const frequency =
    spectrum.frequencyUnitCode === ORDERS_UNIT_CODE
      ? cascadeDisplayFrequency(frequencyHz, ORDERS_UNIT_CODE, referenceRpm)
      : frequencyHz;
  if (frequency === null) return null;
  return interpolateCascadeAmplitude(spectrum.items, frequency);
};

export const convertCascadeTrendSeries = (
  series: readonly ICascadeTrendSeries[],
  targetUnits: TSignalDisplayUnit,
  unitLabel: (unitCode: number) => string,
): ICascadeTrendSeries[] =>
  series.map(item => {
    const sourceUnits = item.unitCode;
    if (!isVibrationUnitCode(sourceUnits) || !isVibrationUnitCode(targetUnits) || sourceUnits === targetUnits) {
      return item;
    }
    return {
      ...item,
      unitCode: targetUnits,
      units: unitLabel(targetUnits),
      samples: item.samples.map(sample => {
        const frequencyHz =
          sample.rotationFrequencyRpm != null && sample.rotationFrequencyRpm > 0 ? sample.rotationFrequencyRpm / 60 : 0;
        if (!(frequencyHz > 0)) return sample;
        return {
          ...sample,
          value: sample.value * getSpectrumAmplitudeFactor(sourceUnits, targetUnits, frequencyHz),
        };
      }),
    };
  });

export const applyCascadeFrequencyProfile = (
  series: readonly ICascadeTrendSeries[],
  spectra: readonly ICascadeSpectrum[],
  frequencyHz: number,
  referenceRpm: number | null,
): ICascadeTrendSeries[] => {
  const spectraByKey = new Map(spectra.map(spectrum => [spectrum.key, spectrum]));
  return series.flatMap(item => {
    let matched = false;
    const samples = item.samples.map(sample => {
      const spectrum = spectraByKey.get(sample.key);
      if (!spectrum) return { ...sample, value: Number.NaN };
      matched = true;
      const amplitude = sampleCascadeSpectrumAmplitude(spectrum, frequencyHz, referenceRpm);
      return { ...sample, value: amplitude ?? Number.NaN };
    });
    if (!matched) return [];
    const spectrum = item.samples.map(sample => spectraByKey.get(sample.key)).find(Boolean);
    return [
      {
        ...item,
        samples,
        unitCode: spectrum?.unitCode ?? item.unitCode,
        units: spectrum?.units ?? item.units,
      },
    ];
  });
};
